import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Globe2, MapPin, Layers, ShieldCheck, Building2, Users2, ArrowRight, CheckCircle2, XCircle,
  Waves, Info, Calendar, Flag,
} from 'lucide-react';
import { NATIONAL } from '@/data/nationalContent';

export default function NationalPage() {
  const { t } = useLang();
  const N = NATIONAL;

  return (
    <div>
      {/* HERO */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <SectionKicker>Pan-India Political Movement</SectionKicker>
              <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {N.title}
              </h1>
              <p className="mt-4 max-w-3xl text-lg text-[#062B5C] font-medium">{N.subtitle}</p>
              <div className="mt-5 space-y-3 max-w-3xl text-[#5B6B82] leading-relaxed">
                {N.intro.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                <Flag className="h-3.5 w-3.5" />
                {N.motto_chain}
              </div>
            </div>
            <div className="lg:col-span-4 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-[#F47B20]/15 via-white to-[#138A4B]/15 blur-2xl" aria-hidden="true" />
                <div className="relative h-56 w-56 sm:h-64 sm:w-64 rounded-full bg-white border border-[#D7E1EF] shadow-lg flex items-center justify-center">
                  <Globe2 className="h-24 w-24 text-[#062B5C]" strokeWidth={1.25} />
                  <div className="absolute inset-0 rounded-full border border-[#F47B20]/20" />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ECI legal distinction */}
      <section className="py-10 bg-white border-y border-[#D7E1EF]">
        <Container>
          <Card className="border-[#F47B20]/30 bg-[#FFF7EE]" data-testid="national-eci-notice">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-white border border-[#F47B20]/30 flex items-center justify-center text-[#F47B20] shrink-0">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{N.eci_notice.heading}</div>
                <p className="mt-2 text-sm text-[#0B1220] leading-relaxed max-w-4xl">{N.eci_notice.body}</p>
              </div>
            </CardContent>
          </Card>
        </Container>
      </section>

      {/* Pan-India architecture — 10 levels */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow="Organisational architecture"
            title="Ten levels — from citizen to national council"
            subtitle="The same institutional model operates across India, adapting to each state's administrative structure."
          />
          <div className="mt-10 space-y-3" data-testid="national-architecture">
            {N.architecture.map((row, i) => (
              <div
                key={row.key}
                className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center rounded-xl border border-[#D7E1EF] bg-white p-4"
                data-testid="national-arch-row"
              >
                <div className="sm:col-span-1">
                  <span className="h-8 w-8 rounded-md bg-[#062B5C] text-white text-xs font-semibold flex items-center justify-center">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="sm:col-span-4">
                  <div className="font-display font-semibold text-[#0B1220] text-sm">{row.level}</div>
                </div>
                <div className="sm:col-span-7 text-sm text-[#5B6B82] leading-relaxed">{row.note}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Principle: National Values — State Solutions — Local Leadership */}
      <section className="py-16 bg-[#062B5C] text-white relative overflow-hidden">
        <TricolourRibbon className="absolute top-0 left-0 right-0" />
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">The core principle</div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-[-0.01em]">
                {N.principle.name}
              </h2>
              <p className="mt-5 text-white/85 leading-relaxed max-w-2xl">{N.principle.body}</p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <PrincipleTile icon={ShieldCheck} title="National Values" body="Common constitutional principles, ethics and transparency across every state." />
                <PrincipleTile icon={Building2} title="State Solutions" body="State-specific policy programmes suited to local realities." />
                <PrincipleTile icon={Users2} title="Local Leadership" body="Empowered village, ward and municipal organisations solving local problems." />
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">Why this matters</div>
              <div className="mt-3 space-y-2">
                {N.principle.contrasts.map((c, i) => (
                  <div key={i} className="rounded-lg bg-white/10 border border-white/15 p-4">
                    <div className="text-sm font-semibold text-white">{c.region}</div>
                    <div className="mt-1 text-xs text-white/80">{c.topic}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* National commitment */}
      <section className="py-16 bg-white border-b border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="Our national commitment"
            title="Ten guarantees NGRP works toward — in every state"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {N.commitments.map((c, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-lg border border-[#D7E1EF] bg-white p-4"
                data-testid="national-commitment"
              >
                <span className="h-7 w-7 rounded-md bg-[#EAF6EF] text-[#138A4B] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </span>
                <div className="text-sm text-[#0B1220] leading-relaxed">{c}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Responsibility split */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Responsibility split" title="What the national, state and local organisations each own" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {N.responsibility_split.map((r) => (
              <Card key={r.layer} className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{r.layer}</div>
                  <p className="mt-3 text-sm text-[#0B1220] leading-relaxed">{r.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* Goal: institution not personality */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Our goal" title={N.goal.heading} />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {N.goal.contrasts.map((c, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#D7E1EF] bg-[#F5F8FC] p-5"
                data-testid="national-goal-contrast"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B54708]">Not this</div>
                <div className="mt-2 flex items-start gap-2 text-sm text-[#5B6B82] line-through decoration-[#B42318]/40">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-[#B42318]/60 shrink-0" />
                  {c.avoid}
                </div>
                <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#138A4B]">This</div>
                <div className="mt-2 flex items-start gap-2 text-sm text-[#0B1220] font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#138A4B] shrink-0" />
                  {c.pursue}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Wave-based expansion */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow="Wave-based expansion"
            title="How NGRP will expand across India"
            subtitle="No simultaneous nationwide launch. No paper committees. Wave-based, evidence-first."
          />
          <div className="mt-10 space-y-4" data-testid="national-expansion-waves">
            {N.expansion_waves.map((w, i) => (
              <div
                key={w.wave}
                className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center rounded-xl border border-[#D7E1EF] bg-white p-5"
              >
                <div className="lg:col-span-2 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#062B5C] text-white flex items-center justify-center font-semibold text-sm">
                    {w.wave}
                  </div>
                  <Waves className="h-4 w-4 text-[#F47B20]" />
                </div>
                <div className="lg:col-span-3">
                  <div className="font-display font-semibold text-[#0B1220] text-sm">{w.title}</div>
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F5F8FC] border border-[#D7E1EF] text-[#062B5C] px-2 py-0.5 text-[11px]">
                    <Calendar className="h-3 w-3" /> {w.when}
                  </div>
                </div>
                <div className="lg:col-span-7 text-sm text-[#0B1220] leading-relaxed">{w.body}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-[#FFF7EE] border border-[#F47B20]/30 p-5 text-sm text-[#8A4B12] leading-relaxed">
            <span className="font-semibold">Expansion principle: </span>{N.expansion_principle}
          </div>
        </Container>
      </section>

      {/* Closing tagline band */}
      <section className="py-16 bg-[#062B5C] text-white">
        <Container>
          <div className="max-w-4xl text-center mx-auto">
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-snug">
              {N.closing.tagline}
            </p>
            <p className="mt-5 text-lg sm:text-xl text-white/90 leading-snug">
              {N.closing.hindi_tagline}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/constitution">
                <Button className="bg-[#F47B20] hover:bg-[#dd6f1d] text-white" data-testid="national-constitution-cta">
                  Read the NGRP Constitution <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/roadmap">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="national-roadmap-cta">
                  See the first-state launch plan
                </Button>
              </Link>
              <Link to="/dialogue">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="national-dialogue-cta">
                  Share reforms India needs
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Next document */}
      <section className="py-10 bg-white">
        <Container>
          <Card className="border-[#D7E1EF] bg-white">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C] shrink-0">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Next document to prepare</div>
                <p className="mt-2 text-sm text-[#0B1220] leading-relaxed max-w-4xl">{N.next_document}</p>
              </div>
            </CardContent>
          </Card>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function PrincipleTile({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/15 p-4">
      <div className="h-8 w-8 rounded-md bg-white/15 flex items-center justify-center text-white">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 font-display font-semibold text-white">{title}</div>
      <p className="mt-2 text-xs text-white/85 leading-relaxed">{body}</p>
    </div>
  );
}
